<div class="p-4 space-y-4">
  <!-- Hero header: soft gradient banner with breadcrumb, title, and action buttons. -->
  <div class="rounded-xl border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-emerald-50 p-4 shadow-sm">
    <!-- Breadcrumb: Fleet › Reports › <agentId>. First segment routes back to
         the Fleet list; the trailing segments are current-page markers, so
         they render as static text rather than links. -->
    <nav aria-label="Breadcrumb"
         class="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
      <a routerLink="/fleet"
         class="inline-flex items-center gap-1 font-medium hover:text-indigo-700 transition-colors">
        <lucide-icon [img]="icons.Bus" [size]="12"></lucide-icon>
        Fleet
      </a>
      <lucide-icon [img]="icons.ChevronRight" [size]="12" class="text-gray-300"></lucide-icon>
      <span class="font-medium text-gray-600">Reports</span>
      <lucide-icon [img]="icons.ChevronRight" [size]="12" class="text-gray-300"></lucide-icon>
      <span class="font-mono text-indigo-700">{{ agentId() }}</span>
    </nav>

    <div class="flex items-start justify-between gap-4 flex-wrap">
      <div class="min-w-0">
        <h1 class="text-xl font-bold text-gray-900 flex items-center gap-2">
          <span class="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-600 text-white shadow-sm">
            <lucide-icon [img]="icons.BarChart3" [size]="20"></lucide-icon>
          </span>
          Fleet Reports
        </h1>
        <p *ngIf="agent() as a" class="mt-1 text-sm text-gray-600 flex flex-wrap items-center gap-2">
          <span class="font-medium">{{ a.name || 'Unnamed tracker' }}</span>
          <span class="rounded-full bg-white/70 border border-gray-200 px-2 py-0.5 text-xs font-mono text-gray-600">
            {{ agentId() }}
          </span>
          <span *ngIf="a.fleetVersion != null"
                class="rounded-full bg-indigo-100 text-indigo-700 px-2 py-0.5 text-xs font-medium">
            Fleet v{{ a.fleetVersion }}
          </span>
        </p>
      </div>
      <div class="flex items-center gap-2">
        <button
          *ngIf="agent()?.fleetVersion === 2"
          type="button"
          (click)="requestGpsData()"
          [disabled]="!canRequestGps()"
          title="Ask the tracker to report its position now"
          class="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-emerald-700 hover:shadow disabled:opacity-40 disabled:cursor-not-allowed transition-all">
          <lucide-icon [img]="icons.Radio" [size]="14"></lucide-icon>
          {{ requestingGps() ? 'Requesting…' : 'Request GPS' }}
        </button>
        <button
          type="button"
          (click)="reload()"
          [disabled]="loading()"
          class="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-indigo-700 hover:shadow disabled:opacity-50 disabled:cursor-not-allowed transition-all">
          <lucide-icon [img]="icons.History" [size]="14"></lucide-icon>
          Refresh
        </button>
      </div>
    </div>
  </div>

  <!-- Date range -->
  <div class="flex flex-wrap items-end gap-3 border border-gray-200 rounded-md p-3 bg-gray-50">
    <div>
      <label class="block text-xs font-medium text-gray-600 mb-1">From</label>
      <input
        type="date"
        [ngModel]="dateFrom()"
        (ngModelChange)="onDateFromChange($event)"
        class="border border-gray-300 rounded-md px-2 py-1 text-sm" />
    </div>
    <div>
      <label class="block text-xs font-medium text-gray-600 mb-1">To</label>
      <input
        type="date"
        [ngModel]="dateTo()"
        (ngModelChange)="onDateToChange($event)"
        class="border border-gray-300 rounded-md px-2 py-1 text-sm" />
    </div>
    <button
      type="button"
      (click)="reload()"
      [disabled]="loading()"
      class="rounded-md bg-gray-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-800 disabled:opacity-50">
      Apply
    </button>
  </div>

  <!-- Latest position + info card. Independent of the report date range —
       the map always shows the freshest known point. -->
  <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
    <div class="lg:col-span-2 border border-gray-200 rounded-md p-3 bg-white">
      <div class="flex items-center gap-2 mb-2">
        <lucide-icon [img]="icons.MapPin" [size]="18" class="text-indigo-600"></lucide-icon>
        <h2 class="text-sm font-semibold">Latest position</h2>
        <button
          type="button"
          (click)="reloadLatestPoint()"
          [disabled]="loadingLatest()"
          class="ml-auto text-xs text-gray-500 hover:text-indigo-700 disabled:opacity-50">
          Refresh
        </button>
      </div>
      <!-- Google Maps mounts here. Empty ref while agent has no data. -->
      <div #mapEl class="h-72 w-full rounded-md border border-gray-200 bg-gray-50"></div>
    </div>
    <div class="border border-gray-200 rounded-md p-3 bg-white">
      <h2 class="text-sm font-semibold mb-2">Details</h2>
      <div *ngIf="loadingLatest()" class="text-sm text-gray-500">Loading latest position…</div>
      <div *ngIf="!loadingLatest() && !latestPoint()" class="text-sm text-gray-500">
        No point has been ingested for this agent yet.
      </div>
      <dl *ngIf="!loadingLatest() && latestPoint() as p" class="text-sm space-y-2">
        <div class="flex items-start gap-2">
          <dt class="w-24 shrink-0 text-gray-500">Status</dt>
          <dd>
            <span class="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
                  [ngClass]="statusClass()">
              {{ statusLabel() }}
            </span>
          </dd>
        </div>
        <div class="flex items-start gap-2">
          <dt class="w-24 shrink-0 text-gray-500">Last activity</dt>
          <dd>
            <!-- fleet-api returns ISO 8601 UTC with a 'Z' suffix; Angular's
                 date pipe converts to the viewer's local timezone by default.
                 The medium format ("Sep 16, 2026, 3:22:31 PM") matches what
                 v2 renders in Manage-Live-Details so operators moving
                 between the two see the same clock. -->
            <div>{{ p.dateTime | date: 'medium' }}</div>
            <div class="text-xs text-gray-400 font-mono">{{ p.dateTime }}</div>
          </dd>
        </div>
        <div class="flex items-start gap-2">
          <dt class="w-24 shrink-0 text-gray-500">Location</dt>
          <dd>
            <div>{{ latestAddress() || (p.lat | number:'1.5-5') + ', ' + (p.lon | number:'1.5-5') }}</div>
            <div class="text-xs text-gray-500 font-mono" *ngIf="latestAddress()">
              {{ p.lat | number:'1.5-5' }}, {{ p.lon | number:'1.5-5' }}
            </div>
          </dd>
        </div>
        <div class="flex items-start gap-2" *ngIf="speedMph() as s">
          <dt class="w-24 shrink-0 text-gray-500">Speed</dt>
          <dd>{{ s }} mph</dd>
        </div>
        <div class="flex items-start gap-2" *ngIf="p.heading !== null && p.heading !== undefined">
          <dt class="w-24 shrink-0 text-gray-500">Heading</dt>
          <dd>{{ p.heading | number:'1.0-0' }}°</dd>
        </div>
        <div class="flex items-start gap-2" *ngIf="p.altitude !== null && p.altitude !== undefined">
          <dt class="w-24 shrink-0 text-gray-500">Altitude</dt>
          <dd>{{ p.altitude | number:'1.0-0' }} m</dd>
        </div>
      </dl>
    </div>
  </div>

  <div *ngIf="loading()" class="my-6"><spinner></spinner></div>

  <ng-container *ngIf="!loading()">
    <!-- Counts row -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div class="border border-gray-200 rounded-md p-3 bg-white">
        <div class="text-xs text-gray-500">Total events</div>
        <div class="text-2xl font-semibold">{{ counts().total }}</div>
      </div>
      <div class="border border-gray-200 rounded-md p-3 bg-white">
        <div class="text-xs text-gray-500">Speeding</div>
        <div class="text-2xl font-semibold text-red-600">{{ counts().speeding }}</div>
      </div>
      <div class="border border-gray-200 rounded-md p-3 bg-white">
        <div class="text-xs text-gray-500">Trip Starts / Stops</div>
        <div class="text-2xl font-semibold">
          <span class="text-green-600">{{ counts().starts }}</span>
          <span class="text-gray-400"> / </span>
          <span class="text-amber-600">{{ counts().stops }}</span>
        </div>
      </div>
      <div class="border border-gray-200 rounded-md p-3 bg-white">
        <div class="text-xs text-gray-500">Geofence / Emergency</div>
        <div class="text-2xl font-semibold">
          <span class="text-sky-600">{{ counts().geofence }}</span>
          <span class="text-gray-400"> / </span>
          <span class="text-orange-600">{{ counts().emergency }}</span>
        </div>
      </div>
    </div>

    <!-- Speed chart + event category chart -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div class="border border-gray-200 rounded-md p-3 bg-white">
        <div class="flex items-center gap-2 mb-2">
          <lucide-icon [img]="icons.Gauge" [size]="18" class="text-indigo-600"></lucide-icon>
          <h2 class="text-sm font-semibold">Daily Speed (avg vs max)</h2>
        </div>
        <div class="relative h-64">
          <canvas *ngIf="speedDays().length > 0"
            baseChart
            [data]="speedChartData()"
            [options]="speedChartOptions"
            type="line">
          </canvas>
          <div *ngIf="speedDays().length === 0"
            class="absolute inset-0 flex items-center justify-center text-sm text-gray-400">
            No speed data in this range.
          </div>
        </div>
      </div>
      <div class="border border-gray-200 rounded-md p-3 bg-white">
        <div class="flex items-center gap-2 mb-2">
          <lucide-icon [img]="icons.Activity" [size]="18" class="text-indigo-600"></lucide-icon>
          <h2 class="text-sm font-semibold">Events by category</h2>
        </div>
        <div class="relative h-64">
          <canvas *ngIf="counts().total > 0"
            baseChart
            [data]="eventChartData()"
            [options]="eventChartOptions"
            type="bar">
          </canvas>
          <div *ngIf="counts().total === 0"
            class="absolute inset-0 flex items-center justify-center text-sm text-gray-400">
            No events in this range.
          </div>
        </div>
      </div>
    </div>

    <!-- Daily speed table -->
    <div class="border border-gray-200 rounded-md bg-white">
      <div class="p-3 border-b border-gray-200 text-sm font-semibold">
        Daily Speed Summary
      </div>
      <div *ngIf="speedDays().length === 0" class="p-6 text-center text-sm text-gray-500">
        No data for this range.
      </div>
      <table *ngIf="speedDays().length > 0" class="w-full text-xs">
        <thead class="bg-blue-50 text-left">
          <tr>
            <th class="px-3 py-2">Date</th>
            <th class="px-3 py-2">Avg speed (mph)</th>
            <th class="px-3 py-2">Max speed (mph)</th>
            <th class="px-3 py-2">Points</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let d of speedDays()" class="border-t border-gray-200 hover:bg-gray-50">
            <td class="px-3 py-1.5 font-mono">{{ d.date }}</td>
            <td class="px-3 py-1.5">{{ d.avgSpeed | number:'1.1-1' }}</td>
            <td class="px-3 py-1.5 text-red-600">{{ d.maxSpeed | number:'1.1-1' }}</td>
            <td class="px-3 py-1.5 text-gray-600">{{ d.pointCount }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Event log table with type filters -->
    <div class="border border-gray-200 rounded-md bg-white">
      <div class="p-3 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
        <div class="text-sm font-semibold">
          Event Log
          <span class="text-xs text-gray-500 font-normal">
            ({{ filteredLogs().length }} of {{ logs().length }})
          </span>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <button *ngFor="let label of eventTypeLabels"
            type="button"
            (click)="toggleType(label)"
            [class.bg-indigo-600]="isTypeSelected(label)"
            [class.text-white]="isTypeSelected(label)"
            [class.bg-white]="!isTypeSelected(label)"
            [class.text-gray-700]="!isTypeSelected(label)"
            class="border border-gray-300 rounded-full px-2.5 py-0.5 text-xs hover:border-indigo-500">
            {{ label }}
          </button>
          <button *ngIf="selectedTypes().size > 0"
            type="button"
            (click)="clearTypeFilters()"
            class="text-xs text-gray-500 hover:text-red-600 underline">
            Clear
          </button>
        </div>
      </div>
      <div *ngIf="filteredLogs().length === 0" class="p-6 text-center text-sm text-gray-500">
        No events match the current filters.
      </div>
      <div *ngIf="filteredLogs().length > 0" class="overflow-x-auto">
        <table class="w-full text-xs">
          <thead class="bg-blue-50 text-left">
            <tr>
              <th class="px-3 py-2">Date</th>
              <th class="px-3 py-2">Vehicle</th>
              <th class="px-3 py-2">Action</th>
              <th class="px-3 py-2">Speed</th>
              <th class="px-3 py-2">Analysis</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of filteredLogs()"
              class="border-t border-gray-200 hover:bg-gray-50">
              <td class="px-3 py-1.5 whitespace-nowrap">
                <!-- fleet-api emits dateTime as ISO 8601 UTC (same shape as
                     /points), so run it through Angular's date pipe to
                     match the operator's local timezone — otherwise the
                     table reads as if every event happened in UTC. -->
                {{ (row.logDate || row.dateTime) | date: 'medium' }}
              </td>
              <td class="px-3 py-1.5">{{ row.vehicle || '—' }}</td>
              <td class="px-3 py-1.5">{{ row.observedData || row.type }}</td>
              <td class="px-3 py-1.5">{{ row.speed || '—' }}</td>
              <td class="px-3 py-1.5 text-gray-600">{{ row.ruleValue || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </ng-container>
</div>
